import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getDashboardCustomTitles, setDashboardCustomTitle } from '../common/CommonChart';
import './KPICard.css';

const CUSTOM_TITLES_EVENT = 'dashboard-custom-titles-applied';

const KPICard = ({ title, value, icon, color, changePercentage, loading, onClick, compactValue, widgetKey, onTitleChange, editableTitle }) => {
    const isPositiveChange = changePercentage >= 0;
    const formattedChange = Math.abs(changePercentage).toFixed(1);
    const titleLookupKey = widgetKey || title;

    const [titleVersion, setTitleVersion] = useState(0);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [draftTitle, setDraftTitle] = useState('');
    const inputRef = useRef(null);

    const customTitles = getDashboardCustomTitles();
    const effectiveTitle = (titleLookupKey && customTitles[titleLookupKey]) || title;
    // eslint-disable-next-line no-unused-vars
    const _titleVersionDep = titleVersion;

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const sync = () => setTitleVersion((v) => v + 1);
        window.addEventListener(CUSTOM_TITLES_EVENT, sync);
        return () => window.removeEventListener(CUSTOM_TITLES_EVENT, sync);
    }, []);

    useEffect(() => {
        if (isEditingTitle && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditingTitle]);

    const startEditing = (e) => {
        if (!editableTitle) return;
        e.stopPropagation();
        setDraftTitle(effectiveTitle || '');
        setIsEditingTitle(true);
    };

    const commitTitle = () => {
        if (!editableTitle) {
            setIsEditingTitle(false);
            return;
        }
        const next = (draftTitle || '').trim();
        setIsEditingTitle(false);
        if (!titleLookupKey) return;
        const normalizedNext = next === title ? '' : next;
        const previous = (getDashboardCustomTitles())[titleLookupKey] || '';
        if (normalizedNext === previous) return;
        setDashboardCustomTitle(titleLookupKey, normalizedNext);
        if (onTitleChange) onTitleChange(titleLookupKey, normalizedNext);
    };

    const cancelEditing = () => setIsEditingTitle(false);

    const renderTitleEditor = () => (
        <input
            ref={inputRef}
            type="text"
            className="kpi-lbl-input"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitTitle}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                    e.preventDefault();
                    commitTitle();
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelEditing();
                }
            }}
            maxLength={60}
            aria-label="Edit widget name"
        />
    );

    const handleTitleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            startEditing(e);
        }
    };

    const renderTitleDisplay = () => {
        const editableProps = editableTitle ? {
            role: 'button',
            tabIndex: 0,
            onKeyDown: handleTitleKeyDown,
            onDoubleClick: startEditing
        } : {};
        return (
            <div
                className={`kpi-lbl${editableTitle ? ' is-editable' : ''}`}
                title={editableTitle ? 'Double-click to rename' : undefined}
                {...editableProps}
            >
                <span>{effectiveTitle}</span>
                {editableTitle && (
                    <button
                        type="button"
                        className="kpi-rename-btn"
                        onClick={startEditing}
                        aria-label="Rename widget"
                        title="Rename widget"
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                        </svg>
                    </button>
                )}
            </div>
        );
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="kpi-skeleton">
                    <div className="skeleton-value"></div>
                    <div className="skeleton-label"></div>
                </div>
            );
        }
        return (
            <>
                <div className={`kpi-val ${compactValue ? 'compact' : ''}`} style={{ color: color }}>
                    {value}
                </div>
                {isEditingTitle ? renderTitleEditor() : renderTitleDisplay()}
                {changePercentage !== undefined && (
                    <div className={`kpi-delta ${isPositiveChange ? 'up' : 'down'}`}>
                        {isPositiveChange ? '▲' : '▼'} {formattedChange}% vs last period
                    </div>
                )}
            </>
        );
    };

    const interactiveProps = (onClick && !isEditingTitle) ? {
        role: 'button',
        tabIndex: 0,
        onClick,
        onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(e);
            }
        }
    } : {};

    return (
        <div
            className={`kpi-card ${loading ? 'loading' : ''}`}
            style={{ '--accent-color': color }}
            {...interactiveProps}
        >
            <div className="kpi-accent" style={{ background: color }}></div>
            <div className="kpi-icon" style={{ background: `${color}20`, color: color }}>
                {icon}
            </div>
            {renderContent()}
        </div>
    );
};

KPICard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    icon: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    changePercentage: PropTypes.number,
    loading: PropTypes.bool,
    onClick: PropTypes.func,
    compactValue: PropTypes.bool,
    widgetKey: PropTypes.string,
    onTitleChange: PropTypes.func,
    editableTitle: PropTypes.bool
};

KPICard.defaultProps = {
    loading: false,
    onClick: () => {},
    compactValue: false,
    editableTitle: false
};

export default KPICard;
